<template>
  <div class="experience-slider-container">
    <PageWrap>
      <h3 class="experience-slider-container__title">{{ title }}</h3>
      <UiSegments
        v-if="showSegments"
        class="experience-slider-container__segments"
        :options="options"
        :value="value"
        @on-select="onSelect"
      />
    </PageWrap>
    <ExpSlider
      class="experience-slider-container__slider"
      :experiences="activeExperiences"
    />
    <PageWrap>
      <div
        v-if="currentShowAllUrl"
        class="full-size-button"
      >
        <UiButtonV3
          :is-full-width="isMobile || isTablet"
          :href="currentShowAllUrl"
          :variant="UiButtonVariant.OUTLINE"
          :color-mode="UiButtonColorMode.NEUTRAL"
          text="Смотреть все"
          @clicked="onViewAllClick"
        />
      </div>
    </PageWrap>
  </div>
</template>

<script setup lang="ts">
  import { UiSegments } from '@experience/core-segments-ui';
  import { UiButtonV3, UiButtonVariant, UiButtonColorMode } from '@experience/vue3-kit';
  import { computed, ref } from 'vue';

  import ExpSlider from '@/entities/aroundV2/ExpSlider.vue';
  import { trackEvent } from '@/shared/lib/analytics/compat';
  import PageWrap from '@/shared/ui/PageWrap.vue';
  import { useScreenSize } from '@/shared/utils/breakpoints';

  import type { Experience } from '@experience/exp-card/dist/types/experience';

  const props = withDefaults(
    defineProps<{
      title?: string;
      fromExperiences?: Experience[];
      toExperiences?: Experience[];
      fromLabel?: string;
      toLabel?: string;
      showAllUrl?: string;
      fromShowAllUrl?: string | null;
      toShowAllUrl?: string | null;
      analyticsBase?: Record<string, unknown>;
    }>(),
    {
      title: 'Выбрать экскурсию',
      fromExperiences: () => [],
      toExperiences: () => [],
      fromLabel: '',
      toLabel: '',
      showAllUrl: '',
      fromShowAllUrl: '',
      toShowAllUrl: '',
      analyticsBase: () => ({}),
    },
  );

  const { isMobile, isTablet } = useScreenSize();

  const options = computed(() => [
    { value: 'to', text: props.toLabel },
    { value: 'from', text: props.fromLabel },
  ]);

  const value = ref('to');

  const showSegments = computed(
    () =>
      props.toExperiences.length > 0 &&
      props.fromExperiences.length > 0 &&
      Boolean(props.toLabel) &&
      Boolean(props.fromLabel),
  );

  const activeExperiences = computed<Experience[]>(() => {
    if (value.value === 'from' && props.fromExperiences.length) {
      return props.fromExperiences;
    }

    if (props.toExperiences.length) {
      return props.toExperiences;
    }

    return props.fromExperiences;
  });

  // Ссылка «Смотреть все» зависит от выбранного сегмента, с запасным вариантом showAllUrl.
  const currentShowAllUrl = computed(() => {
    const link = value.value === 'from' ? props.fromShowAllUrl : props.toShowAllUrl;

    return link || props.showAllUrl;
  });

  // Общие параметры для событий экскурсий: город/страна страницы + точка отправления.
  // Точка отправления зависит от выбранного сегмента:
  // 'to' — старт в городе назначения (subject_geo), 'from' — старт в городе страницы (city).
  const experienceParams = (selected: string) => {
    const base = props.analyticsBase;
    const isTo = selected === 'to';

    return {
      city_id: base.city_id as number | undefined,
      city_name: base.city_name as string | undefined,
      country_id: base.country_id as number | undefined,
      country_name: base.country_name as string | undefined,
      start_geo_id: (isTo ? base.subject_geo_id : base.city_id) as number | undefined,
      start_geo_name: (isTo ? base.subject_geo_name : base.city_name) as string | undefined,
    };
  };

  const onSelect = (selected: string) => {
    value.value = selected;

    void trackEvent('around:experiences:toggle.click', experienceParams(selected));
  };

  const onViewAllClick = () => {
    void trackEvent('around:experiences:view-all.click', experienceParams(value.value));
  };
</script>

<style scoped lang="scss">
  .experience-slider-container {
    padding-top: 40px;

    &__title {
      @include base-700-700-semi;

      @include mobile {
        @include base-500-500-semi;
      }
    }

    &__segments {
      margin-top: 24px;

      @include mobile {
        margin-top: 16px;
      }
    }

    &__slider {
      margin-top: 24px;
    }

    @include mobile {
      padding-top: 32px;
    }
  }

  .full-size-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin-top: 24px;
  }
</style>
